import MultiSelectInputFreeSolo from '@brands/Components/Inputs/MuiMultiSelectInputFreeSolo/MultiSelectInputFreeSolo';
import MultiselectInput from '@brands/Components/Inputs/MultiselectInput/MultiselectInput';
import { OptionCdts, OralHealthReportSubmit } from '@brands/Dashboard/ConsultationPage/assets/types';
import { useWindowSize } from '@brands/hooks';
import { CaseType, ICase } from '@brands/services/cases/types/ICase';
import { ICDT } from '@brands/services/oneReport/types/IOneReport';
import { customStylesDashboard } from '@brands/Utils/customStyles';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import React, { SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import Modal, { Styles } from 'react-modal';

import styles from './styles.module.scss';

export type ServicesProvidedFormData = {
  selectedCDTCodes: string[];
  selectedICDCodes: string[];
};

const normalizeCodes = (codes: unknown[]): string[] =>
  codes
    .filter((c): c is string => typeof c === 'string')
    .map((c) => c.split(':')[0].trim())
    .filter(Boolean);

type ServicesProvidedModalProps = {
  open: boolean;
  onClose: () => void;
  consultationForm: OralHealthReportSubmit;
  setConsultationForm: React.Dispatch<SetStateAction<OralHealthReportSubmit>>;
  thisCase: ICase;
  setCase: React.Dispatch<SetStateAction<ICase>>;
  originalCDTCodes: ICDT[];
  cdtOptions: OptionCdts[];
  setCdtOptions: React.Dispatch<React.SetStateAction<OptionCdts[]>>;
  evaluationCodes: string[];
  saveCaseCDT: (codes: string[]) => Promise<void>;
};

const ServicesProvidedModal: React.FC<ServicesProvidedModalProps> = ({
  open,
  onClose,
  consultationForm,
  setConsultationForm,
  thisCase,
  setCase,
  originalCDTCodes,
  cdtOptions,
  setCdtOptions,
  evaluationCodes,
  saveCaseCDT,
}) => {
  const screenSize = useWindowSize();
  const isMobile = useMemo(() => screenSize.width < 768, [screenSize.width]);

  const [otherCDTCodes, setOtherCDTCodes] = useState<string[]>([]);
  const [tempSelectedCDTCodes, setTempSelectedCDTCodes] = useState<string[]>([]);
  const [tempSelectedICDCodes, setTempSelectedICDCodes] = useState<string[]>([]);
  const [selectedEvaluationCode, setSelectedEvaluationCode] = useState<string | null>(null);

  const enforcedTeledentistryCode = useMemo(() => {
    if (
      thisCase?.type === 'video_call_scheduled' ||
      thisCase?.type === 'video_call_instant' ||
      thisCase?.type === CaseType.phone_call_scheduled
    ) {
      return 'D9995';
    }
    return 'D9996';
  }, [thisCase?.type]);

  const oppositeTeledentistryCode = enforcedTeledentistryCode === 'D9995' ? 'D9996' : 'D9995';

  const getFilteredOptions = useCallback(
    (type: 'CDT' | 'ICD' | null = null) => {
      return cdtOptions
        .filter((opt) => (type ? opt.type === type : true))
        .filter((option) => {
          if (!selectedEvaluationCode) return true;
          if (evaluationCodes.includes(option.value)) {
            return option.value === selectedEvaluationCode;
          }
          return true;
        });
    },
    [cdtOptions, selectedEvaluationCode, evaluationCodes],
  );

  const preparedCDTOptions = useMemo(
    () =>
      getFilteredOptions('CDT').map((opt) => ({
        id: opt.value,
        value: opt.value,
        label: opt.label,
      })),
    [getFilteredOptions],
  );

  const preparedICDOptions = useMemo(
    () =>
      getFilteredOptions('ICD').map((opt) => ({
        id: opt.value,
        value: opt.value,
        label: opt.label,
      })),
    [getFilteredOptions],
  );

  const validCDTCodes = useMemo(
    () => new Set(cdtOptions.filter((opt) => opt.type === 'CDT').map((opt) => opt.value)),
    [cdtOptions],
  );

  const validICDCodes = useMemo(
    () => new Set(cdtOptions.filter((opt) => opt.type === 'ICD').map((opt) => opt.value)),
    [cdtOptions],
  );

  const handleCDTChange = useCallback(
    (newCodes: string[]) => {
      let codes = [...new Set(newCodes.map((c) => c.split(':')[0].trim()).filter(Boolean))];

      codes = codes.filter((c) => validCDTCodes.has(c) && c !== oppositeTeledentistryCode);

      const evaluationSelected = codes.filter((c) => evaluationCodes.includes(c));
      const nonEvaluation = codes.filter((c) => !evaluationCodes.includes(c));

      const evaluationToKeep = evaluationSelected.length > 0 ? evaluationSelected[evaluationSelected.length - 1] : null;

      const finalCodes = evaluationToKeep ? [...nonEvaluation, evaluationToKeep] : nonEvaluation;

      setTempSelectedCDTCodes(finalCodes);
      setSelectedEvaluationCode(evaluationToKeep);

      setOtherCDTCodes(finalCodes.filter((c) => c !== enforcedTeledentistryCode && !evaluationCodes.includes(c)));
    },
    [validCDTCodes, oppositeTeledentistryCode, evaluationCodes, enforcedTeledentistryCode],
  );

  const handleICDChange = useCallback(
    (newCodes: string[]) => {
      const cleaned = [...new Set(newCodes.map((c) => c.split(':')[0].trim()).filter(Boolean))].filter(
        (c) => validICDCodes.has(c) && c !== oppositeTeledentistryCode,
      );

      setTempSelectedICDCodes(cleaned);
    },
    [validICDCodes, oppositeTeledentistryCode],
  );

  const handleSave = async () => {
    const combined = [...tempSelectedCDTCodes, ...tempSelectedICDCodes];

    if (!combined.includes(enforcedTeledentistryCode)) {
      combined.unshift(enforcedTeledentistryCode);
    }

    const allSelectedCodes = [...new Set(combined)];

    await saveCaseCDT(allSelectedCodes);

    setCase((prev) => ({
      ...prev,
      cdts: allSelectedCodes.map((code) => {
        const found = originalCDTCodes.find((c) => c.code === code);
        return {
          code,
          nomenclature: found?.nomenclature || '',
          type: found?.type || 'CDT',
        };
      }),
      one_report: {
        ...prev.one_report,
        assessments: consultationForm.assessment || '',
      },
    }));

    setConsultationForm((prev) => ({
      ...prev,
      cdt: allSelectedCodes,
    }));

    onClose();
  };

  const canShow = useCallback(
    (code: string, type: 'CDT' | 'ICD' = 'CDT') => originalCDTCodes.some((f) => f.code === code && f.type === type),
    [originalCDTCodes],
  );

  useEffect(() => {
    if (!consultationForm?.cdt?.length) {
      setTempSelectedCDTCodes([]);
      setTempSelectedICDCodes([]);
      setSelectedEvaluationCode(null);
      setOtherCDTCodes([]);
      return;
    }

    const allSavedCodes = [...new Set(consultationForm.cdt)];

    const isCDT = (code: string) => code.startsWith('D');
    const isEvaluation = (code: string) => evaluationCodes.includes(code);

    const savedCDTCodes = allSavedCodes.filter(isCDT);
    const savedICDCodes = allSavedCodes.filter((code) => !isCDT(code));

    const cleanedCDT = savedCDTCodes.filter((c) => c !== oppositeTeledentistryCode);

    const evaluationInCDT = cleanedCDT.find(isEvaluation) || null;
    setSelectedEvaluationCode(evaluationInCDT);

    const nonEvalCDT = cleanedCDT.filter((c) => !isEvaluation(c));
    const finalCDT = evaluationInCDT ? [...nonEvalCDT, evaluationInCDT] : nonEvalCDT;

    setTempSelectedCDTCodes(finalCDT);

    setOtherCDTCodes(finalCDT.filter((c) => c !== enforcedTeledentistryCode && !isEvaluation(c)));

    const validSavedICD = savedICDCodes.filter((code) => cdtOptions.some((o) => o.type === 'ICD' && o.value === code));

    setTempSelectedICDCodes(validSavedICD);
  }, [consultationForm.cdt, oppositeTeledentistryCode, enforcedTeledentistryCode, evaluationCodes, cdtOptions]);

  const hasChanges = useMemo(() => {
    const combined = [...tempSelectedCDTCodes, ...tempSelectedICDCodes];
    if (!combined.includes(enforcedTeledentistryCode)) {
      combined.unshift(enforcedTeledentistryCode);
    }
    const wouldSave = [...new Set(combined)].sort();

    // Normalize saved codes the same way the init effect does: add enforced, strip opposite
    const savedBase = [...new Set(consultationForm.cdt || [])];
    if (!savedBase.includes(enforcedTeledentistryCode)) {
      savedBase.unshift(enforcedTeledentistryCode);
    }
    const savedNormalized = [...new Set(savedBase.filter((c) => c !== oppositeTeledentistryCode))].sort();

    return JSON.stringify(wouldSave) !== JSON.stringify(savedNormalized);
  }, [tempSelectedCDTCodes, tempSelectedICDCodes, enforcedTeledentistryCode, oppositeTeledentistryCode, consultationForm.cdt]);

  const cdtValue = useMemo(() => {
    let codes = [...tempSelectedCDTCodes];

    if (!codes.includes(enforcedTeledentistryCode)) {
      codes = [enforcedTeledentistryCode, ...codes];
    }

    return codes.map((code) => ({
      id: code,
      value: code,
      label: code,
    }));
  }, [tempSelectedCDTCodes, enforcedTeledentistryCode]);

  const icdValue = useMemo(
    () =>
      tempSelectedICDCodes.map((code) => ({
        id: code,
        value: code,
        label: code,
      })),
    [tempSelectedICDCodes],
  );

  const modalStyles: Styles = isMobile
    ? {
        ...customStylesDashboard,
        content: {
          ...customStylesDashboard.content,
          width: '100vw',
          height: '100vh',
          borderRadius: 0,
          padding: '90px 16px 32px 16px',
        },
        overlay: {
          ...(customStylesDashboard.overlay || {}),
          overflowY: 'auto',
        },
      }
    : {
        ...customStylesDashboard,
        content: {
          ...customStylesDashboard.content,
          width: '444px',
          maxHeight: '750px',
          overflowY: 'auto',
        },
      };

  return (
    <Modal isOpen={open} onRequestClose={onClose} ariaHideApp={false} style={modalStyles}>
      <div className={`${styles.body} ${styles.formContainer}`}>
        <div className={styles.servicesProvidedModal}>
          <div className={styles.bodyHead}>
            <CloseIcon onClick={onClose} />
          </div>

          <div className={`${styles.body} ${styles.formContainer}`}>
            <span className={styles.bodyHeader}>Add CDT treatment codes</span>
            <span className={styles.bodyHeaderSub}>Please include at least one CDT evaluation code</span>
          </div>

          <div className={styles.container}>
            <div className={styles.autoComplete}>
              {/* CDT Section */}
              <div className={styles.cDT}>
                <div className={`${styles.cDTDiv} fs-unmask`}>
                  <span>Select one evaluation code from the drop-down.</span>
                </div>
                <div className={`${styles.cDTDiv} fs-unmask`}>
                  <span>Additionally, add other service codes provided and documented.</span>
                </div>

                <MultiSelectInputFreeSolo
                  name="cdt"
                  options={preparedCDTOptions}
                  value={cdtValue}
                  onChange={handleCDTChange}
                  styledTag
                  displayCheckbox={false}
                  disabled={false}
                  freeSolo={false}
                />
              </div>

              {/* CDT preview / fixed + selected */}
              <div className={styles.cdtChildren}>
                <div className={styles.fixedValue}>
                  <div className={`fs-exclude ${styles.boldText}`}>
                    {enforcedTeledentistryCode}
                    <span className={styles.noteText}> (Code cannot be deleted)</span>
                  </div>
                  <div className="fs-exclude">
                    {enforcedTeledentistryCode === 'D9995'
                      ? 'synchronous; real-time encounter'
                      : 'Asynchronous; information stored and forwarded to dentist for subsequent review'}
                  </div>
                </div>

                {selectedEvaluationCode && canShow(selectedEvaluationCode, 'CDT') && (
                  <div className={styles.evaluationCodesDiv}>
                    <div className={styles.evaluationCodes}>
                      <div className={styles.evaluationCodesInfo}>
                        <div className={`fs-exclude ${styles.boldText}`}>{selectedEvaluationCode}</div>
                        <div className="fs-exclude">
                          {
                            originalCDTCodes.find((c) => c.code === selectedEvaluationCode && c.type === 'CDT')
                              ?.nomenclature
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {otherCDTCodes
                  .filter((code) => canShow(code, 'CDT') && !evaluationCodes.includes(code))
                  .map((code) => (
                    <div key={code} className={styles.otherCodesDiv}>
                      <div className={styles.otherCodes}>
                        <div className={styles.evaluationCodesInfo}>
                          <div className={`fs-exclude ${styles.boldText}`}>{code}</div>
                          <div className="fs-exclude">
                            {originalCDTCodes.find((c) => c.code === code && c.type === 'CDT')?.nomenclature}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>

              {/* ICD Section */}
              <div className={styles.cDT}>
                <div className={`${styles.cDTDiv} fs-unmask ${styles.icdContainer}`}>
                  <div className={styles.helpWrapper}>
                    <div className={styles.icdHint}>
                      <span className={styles.icdText}>Select one or more ICD-10 codes from the drop-down.</span>
                      <button type="button" className={styles.tooltipTrigger} aria-label="What is an ICD-10 code?">
                        <span className={styles.tooltip}>
                          Select ICD-10 codes that best align with the services you've provided during this case.{' '}
                        </span>{' '}
                        What’s an ICD-10 code?
                      </button>
                    </div>
                  </div>
                </div>

                <MultiSelectInputFreeSolo
                  name="icd"
                  options={preparedICDOptions}
                  value={icdValue}
                  onChange={handleICDChange}
                  styledTag
                  displayCheckbox={false}
                  freeSolo={false}
                />
              </div>
            </div>
          </div>
          <div className={styles.bodyFooter}>
            <Button variant="contained" className={styles.containedBtn} onClick={handleSave} disabled={!hasChanges}>
              Add CDT treatment codes
            </Button>

            <Button variant="text" className={styles.textBtn} onClick={onClose}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ServicesProvidedModal;
